import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts'
import { TrendingUp } from 'lucide-react'
import {
  DAILY_GROSS_TARGET_ZAR,
  formatInt,
  formatRand,
  formatRandCompact,
  type Period,
  type TrendPoint,
} from '../../utils/performance'

const LINE_COLOR = 'hsl(var(--perf-uber-gross))'

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload || payload.length === 0) return null
  const point = payload[0]?.payload as TrendPoint | undefined
  if (!point) return null
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-md">
      <div className="mb-1 font-medium text-popover-foreground">{label}</div>
      <div className="flex items-center justify-between gap-4">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: LINE_COLOR }}
          />
          Earnings / shift
        </span>
        <span className="font-medium tabular-nums text-popover-foreground">
          {formatRand(point.earnings_per_shift)}
        </span>
      </div>
      <div className="mt-0.5 flex items-center justify-between gap-4">
        <span className="text-muted-foreground">Shifts</span>
        <span className="tabular-nums text-popover-foreground">
          {formatInt(point.shifts)}
        </span>
      </div>
    </div>
  )
}

export function EarningsPerShiftChart({
  data,
  period,
  loading,
}: {
  data: TrendPoint[]
  period: Period
  loading: boolean
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-retool-sm">
      <div className="mb-3 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">
          Earnings per shift
          <span className="ml-1 font-normal text-muted-foreground">
            · by {period === 'total' ? 'total' : period} · excludes cancelled
          </span>
        </h2>
      </div>
      {loading ? (
        <div className="flex h-[320px] items-center justify-center text-sm text-muted-foreground">
          Loading…
        </div>
      ) : data.length === 0 ? (
        <div className="flex h-[320px] items-center justify-center text-sm text-muted-foreground">
          No data for the selected filters.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis
              dataKey="bucket"
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              minTickGap={16}
            />
            <YAxis
              tickFormatter={formatRandCompact}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={64}
            />
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'hsl(var(--accent))' }} />
            <ReferenceLine
              y={DAILY_GROSS_TARGET_ZAR}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="5 4"
              label={{
                value: `Target ${formatRand(DAILY_GROSS_TARGET_ZAR)}`,
                position: 'insideTopRight',
                fill: 'hsl(var(--muted-foreground))',
                fontSize: 11,
              }}
            />
            <Line
              type="monotone"
              dataKey="earnings_per_shift"
              name="Earnings / shift"
              stroke={LINE_COLOR}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}
